// WHAT: Card component for displaying top category (workspace/aspect) by time
// WHY:  Provides quick overview of most time-consuming category
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useFilter hook, formatMinutes utility, core.invoke
// RISKS:
//   - NOTE: Backend response structure may not match expected format
//   - NOTE: Error handling may not be user-friendly
//   - NOTE: Loading state may flash on filter changes

import { core } from "@tauri-apps/api";
import { useState, useEffect } from "react";

import { Card } from "@/components/ui";

import { formatMinutes } from "../../utils/format";
import { useFilter } from "./FilterContextProvider.shared";

/**
 * Top category interface
 */
interface TopCategory {
  /** Aspect name (optional) */
  aspect?: string;
  /** Total minutes */
  minutes: number;
  /** Workspace name */
  workspace: string;
}

/**
 * WHAT:
 *   Card component that displays the top category (workspace/aspect) by total
 *   time spent for the selected filter period.
 *
 * WHY:
 *   Provides quick insight into which category receives the most time investment.
 *
 * BEHAVIOR:
 *   - Fetches aggregated summary from backend
 *   - Extracts top category from response
 *   - Displays workspace name and formatted time
 *   - Updates when filter changes
 *   - Shows loading and error states
 *
 * RISKS:
 *   - NOTE: Backend response structure may not match expected format
 *   - NOTE: Error handling may not be user-friendly
 *   - NOTE: Loading state may flash on filter changes
 *
 * @returns {JSX.Element} The top category card component
 *
 */
export const TopCategoryCard = () => {
  const { dateFilter, period } = useFilter();

  const [topCategory, setTopCategory] = useState<null | TopCategory>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await core.invoke<{ top_category: null | TopCategory }>(
          "get_aggregated_summary",
          { filter: dateFilter },
        );
        setTopCategory(result.top_category);
      } catch {
        // NOTE: Error loading data
        setError("Fehler beim Laden der Daten");
        setTopCategory(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [dateFilter]);

  const renderContent = () => {
    if (loading) {
      return <span className="text-slate-500 animate-pulse">Loading…</span>;
    }
    if (error) {
      return <span className="text-red-400 text-lg">Error</span>;
    }
    if (!topCategory) {
      return <span className="text-slate-500">–</span>;
    }

    return (
      <>
        <div className="text-lg text-white font-semibold">{topCategory.workspace}</div>
        <div className="text-2xl text-white font-bold">{formatMinutes(topCategory.minutes)}</div>
      </>
    );
  };

  return (
    <Card
      className="bg-slate-800/30 border-slate-600/50 shadow-tracq-card"
      padding="lg"
      variant="elevated"
    >
      <div className="space-y-2">
        <div className="text-sm text-slate-400">Top Category ({period})</div>
        {renderContent()}
      </div>
    </Card>
  );
};
